import { Alert, Stack, Text } from "@sembark-travel/ui-base"
import {
	FileInputField,
	GetFieldValue,
	MarkdownInputField,
	SwitchInputField,
	TextInputField,
} from "@sembark-travel/ui-form"
import { Link } from "@sembark-travel/ui-router"
import { generatePath } from "../../router-utils"
import { formatBytes } from "./store"

/** Mirrors the server's allowed attachment types. */
const ATTACHMENT_ACCEPT =
	".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.jpg,.jpeg,.png,.gif,.webp,.heic,.zip"

const MAX_ATTACHMENTS = 10

function emailListHelp({ value }: { value?: unknown }) {
	const emails = String(value || "")
		.split(",")
		.map((email) => email.trim())
		.filter(Boolean)
	return (
		"Comma-separated email addresses. " +
		(emails.length ? "<" + emails.join(">, <") + ">" : "")
	)
}

/**
 * The To / Cc / Subject / Message / attachments / signature fields of an
 * email composer. Render inside a `Form` whose values carry these field
 * names (`attachments` holds the picked FileList).
 */
export function EmailComposerFields({
	hasEmailSignature,
	maxAttachmentBytes,
	disabled,
}: {
	hasEmailSignature: boolean
	maxAttachmentBytes: number
	disabled: boolean
}) {
	return (
		<Stack gap="4">
			<TextInputField
				name="to"
				label="To"
				required
				placeholder="email@example.com"
				help={emailListHelp}
				disabled={disabled}
			/>
			<TextInputField
				name="cc"
				label="Cc"
				placeholder="email@example.com, other@example.com"
				help={emailListHelp}
				disabled={disabled}
			/>
			<TextInputField
				name="subject"
				label="Subject"
				required
				disabled={disabled}
			/>
			<MarkdownInputField
				name="body"
				label="Message"
				required
				placeholder="Write your message in markdown"
			/>
			<Stack gap="1">
				<FileInputField
					name="attachments"
					label="Attachments"
					multiple
					accept={ATTACHMENT_ACCEPT}
					disabled={disabled}
					help={`Up to ${MAX_ATTACHMENTS} files, ${formatBytes(maxAttachmentBytes)} in all. Picking again replaces the files.`}
				/>
				<GetFieldValue<FileList | null> name="attachments">
					{({ value }) => (
						<SelectedAttachments
							files={value ? Array.from(value) : []}
							maxAttachmentBytes={maxAttachmentBytes}
						/>
					)}
				</GetFieldValue>
			</Stack>
			<Stack gap="1">
				<SwitchInputField
					name="include_signature"
					label="Include my email signature"
					disabled={disabled || !hasEmailSignature}
				/>
				{!hasEmailSignature ? (
					<Text fontSize="sm" color="muted">
						You have no email signature yet.{" "}
						<Link
							to={`${generatePath("/settings")}#email-signature`}
							anchored
							color="accent"
						>
							Set up your email signature
						</Link>
						.
					</Text>
				) : null}
			</Stack>
		</Stack>
	)
}

/** The picked files with their sizes, and a warning past the limits. */
function SelectedAttachments({
	files,
	maxAttachmentBytes,
}: {
	files: Array<File>
	maxAttachmentBytes: number
}) {
	if (!files.length) {
		return null
	}
	const totalBytes = files.reduce((total, file) => total + file.size, 0)
	return (
		<Stack gap="1">
			{files.map((file) => (
				<Text key={file.name} fontSize="sm" color="muted">
					{file.name} ({formatBytes(file.size)})
				</Text>
			))}
			{files.length > MAX_ATTACHMENTS ? (
				<Alert status="warning" inline>
					Attach at most {MAX_ATTACHMENTS} files.
				</Alert>
			) : maxAttachmentBytes > 0 && totalBytes > maxAttachmentBytes ? (
				<Alert status="warning" inline>
					{formatBytes(totalBytes)} in all is over the{" "}
					{formatBytes(maxAttachmentBytes)} limit.
				</Alert>
			) : null}
		</Stack>
	)
}
